import React, { useState, useEffect } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from '../ui/button';
import { useNavigate } from 'react-router-dom';

const HeroSection = ({ data }) => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const slides = data?.slides || [];
  const navigate = useNavigate();

  useEffect(() => {
    if (slides.length === 0) return;
    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % slides.length);
    }, 5000);
    return () => clearInterval(timer);
  }, [slides.length]);

  const goToSlide = (index) => {
    setCurrentSlide(index);
  };

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % slides.length);
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + slides.length) % slides.length);
  };

  // Handle CTA link clicks
  const handleLinkClick = (e, link) => {
    if (!link) return;
    
    e.preventDefault();
    
    // Check if it's an anchor link
    if (link.startsWith('#')) {
      const targetElement = document.getElementById(link.substring(1));
      if (targetElement) {
        targetElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }
    // Check if it's an internal link (starts with /)
    else if (link.startsWith('/')) {
      navigate(link);
    }
    // External link
    else {
      window.location.href = link;
    }
  };

  return (
    <div className="relative min-h-screen flex items-center justify-center overflow-hidden bg-white">
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute top-20 left-10 w-72 h-72 bg-gray-100 rounded-full mix-blend-multiply filter blur-3xl opacity-30 animate-blob"></div>
        <div className="absolute top-40 right-10 w-72 h-72 bg-gray-200 rounded-full mix-blend-multiply filter blur-3xl opacity-30 animate-blob animation-delay-2000"></div>
        <div className="absolute bottom-20 left-1/2 w-72 h-72 bg-gray-100 rounded-full mix-blend-multiply filter blur-3xl opacity-30 animate-blob animation-delay-4000"></div>
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-32 text-center">
        <div className="mb-12 space-y-6 animate-fade-in">
          <h1 className="text-5xl md:text-7xl font-bold tracking-tight">
            <span className="block text-gray-900">{data?.headline?.split(' ').slice(0, -3).join(' ') || "NYC's Premier"}</span>
            <span className="block text-gray-900">
              {data?.headline?.split(' ').slice(-3).join(' ') || "Tech Community"}
            </span>
          </h1>
          <p className="text-xl md:text-2xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
            {data?.subheadline || "Connecting innovators, entrepreneurs, and tech enthusiasts"}
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center mt-8">
            <Button 
              size="lg" 
              className="bg-black hover:bg-gray-800 text-white px-8 py-6 text-lg"
              onClick={(e) => handleLinkClick(e, data?.primary_cta_link)}
            >
              {data?.primary_cta_label || "Join Our Community"}
            </Button>
            <Button 
              size="lg" 
              variant="outline" 
              className="border-2 border-black text-black hover:bg-gray-50 px-8 py-6 text-lg"
              onClick={(e) => handleLinkClick(e, data?.secondary_cta_link)}
            >
              {data?.secondary_cta_label || "Explore Events"}
            </Button>
          </div>
        </div>

        {slides.length > 0 && (
          <div className="relative max-w-5xl mx-auto mt-16">
            <div className="relative h-96 rounded-2xl overflow-hidden shadow-2xl">
              {slides.map((slide, index) => (
                <div
                  key={slide.id}
                  className={`absolute inset-0 transition-all duration-700 transform ${
                    index === currentSlide
                      ? 'opacity-100 translate-x-0'
                      : index < currentSlide
                      ? 'opacity-0 -translate-x-full'
                      : 'opacity-0 translate-x-full'
                  }`}
                >
                  <img
                    src={slide.image}
                    alt={slide.title}
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent flex flex-col justify-end p-8">
                    <h3 className="text-3xl md:text-4xl font-bold text-white mb-2">
                      {slide.title}
                    </h3>
                    <p className="text-lg md:text-xl text-white/90 mb-4">{slide.subtitle}</p>
                    {slide.button_text && slide.button_link && (
                      <div>
                        <Button
                          size="lg"
                          className="bg-white hover:bg-gray-100 text-gray-900 px-6 py-3 text-base font-medium"
                          onClick={(e) => handleLinkClick(e, slide.button_link)}
                        >
                          {slide.button_text}
                        </Button>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>

            <button
              onClick={prevSlide}
              className="absolute left-4 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-white p-3 rounded-full shadow-lg transition-all duration-200 hover:scale-110"
            >
              <ChevronLeft className="text-gray-800" size={24} />
            </button>
            <button
              onClick={nextSlide}
              className="absolute right-4 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-white p-3 rounded-full shadow-lg transition-all duration-200 hover:scale-110"
            >
              <ChevronRight className="text-gray-800" size={24} />
            </button>

            <div className="flex justify-center gap-2 mt-6">
              {slides.map((_, index) => (
                <button
                  key={index}
                  onClick={() => goToSlide(index)}
                  className={`h-2 rounded-full transition-all duration-300 ${
                    index === currentSlide ? 'w-8 bg-gray-900' : 'w-2 bg-gray-300 hover:bg-gray-400'
                  }`}
                />
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default HeroSection;
